/**
 * TopicResourcesSection — L.1 knowledge accumulator inside TopicDetailPanel.
 *
 * Lists resources (stat / quote / case_study / example / link / image / note)
 * attached to a Topic. Each resource gives the AI Master Post handler a
 * concrete fact/quote/case to weave into the platform-agnostic master post,
 * which derivatives then inherit.
 *
 * Reads via `useTopicResources` (real-time). Writes via `ResourceService`.
 *
 * Mounted inside the Topic detail panel beneath KEY MESSAGE / PILLARS so
 * users see resources at a glance and can fill them before generating.
 */

import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  IconButton,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import LinkIcon from '@mui/icons-material/Link';
import { useTopicResources } from '../../hooks/useTopicResources';
import { ResourceService } from '../../services/ResourceService';
import type { Resource, ResourceReliability, ResourceType } from '../../types';

const RESOURCE_TYPE_LABEL: Record<ResourceType, string> = {
  stat: 'Stat',
  quote: 'Quote',
  case_study: 'Case study',
  example: 'Example',
  link: 'Link',
  image: 'Image',
  note: 'Note',
};

const RELIABILITY_COLOR: Record<ResourceReliability, 'success' | 'warning' | 'default'> = {
  verified: 'success',
  unverified: 'warning',
  internal: 'default',
};

const TESTID = 'topic-resources-section';

export interface TopicResourcesSectionProps {
  projectId: string;
  topicId: string;
  /** Compact mode — used inside a side panel (no Paper wrapper). */
  inline?: boolean;
}

export const TopicResourcesSection: React.FC<TopicResourcesSectionProps> = ({
  projectId,
  topicId,
  inline = false,
}) => {
  const { resources, loading } = useTopicResources(projectId, topicId);
  const service = React.useMemo(() => new ResourceService(), []);
  const [error, setError] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [draftType, setDraftType] = useState<ResourceType>('stat');
  const [draftContent, setDraftContent] = useState('');
  const [draftSourceUrl, setDraftSourceUrl] = useState('');
  const [draftReliability, setDraftReliability] = useState<ResourceReliability>('unverified');
  const [saving, setSaving] = useState(false);

  const reset = () => {
    setDraftType('stat');
    setDraftContent('');
    setDraftSourceUrl('');
    setDraftReliability('unverified');
  };

  const handleSave = async () => {
    if (!draftContent.trim()) return;
    setSaving(true);
    setError(null);
    try {
      await service.create(projectId, topicId, {
        topicId,
        type: draftType,
        content: draftContent.trim(),
        sourceUrl: draftSourceUrl.trim() || undefined,
        reliability: draftReliability,
      });
      reset();
      setAdding(false);
    } catch (e: any) {
      setError(e?.message ?? 'Failed to save resource');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (r: Resource) => {
    setError(null);
    try {
      await service.delete(projectId, topicId, r.id);
    } catch (e: any) {
      setError(e?.message ?? 'Failed to delete resource');
    }
  };

  const body = (
    <>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{ mb: 1.5 }}
      >
        <Box>
          <Typography variant={inline ? 'subtitle2' : 'h6'}>
            Resources
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Stats, quotes, examples — Master post AI cites these in generated posts
          </Typography>
        </Box>
        {!adding && (
          <Button
            size="small"
            startIcon={<AddIcon />}
            onClick={() => setAdding(true)}
            data-testid="add-resource-btn"
          >
            Add
          </Button>
        )}
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 1.5 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {adding && (
        <Box
          sx={{
            p: 2,
            mb: 2,
            border: 1,
            borderColor: 'divider',
            borderRadius: 1,
          }}
          data-testid="add-resource-form"
        >
          <Stack spacing={1.5}>
            <Stack direction="row" spacing={1.5}>
              <Select
                size="small"
                value={draftType}
                onChange={(e) => setDraftType(e.target.value as ResourceType)}
                sx={{ minWidth: 140 }}
                data-testid="resource-type-select"
              >
                {(Object.keys(RESOURCE_TYPE_LABEL) as ResourceType[]).map((k) => (
                  <MenuItem key={k} value={k}>
                    {RESOURCE_TYPE_LABEL[k]}
                  </MenuItem>
                ))}
              </Select>
              <Select
                size="small"
                value={draftReliability}
                onChange={(e) => setDraftReliability(e.target.value as ResourceReliability)}
                sx={{ minWidth: 140 }}
                data-testid="resource-reliability-select"
              >
                <MenuItem value="verified">Verified</MenuItem>
                <MenuItem value="unverified">Unverified</MenuItem>
                <MenuItem value="internal">Internal</MenuItem>
              </Select>
            </Stack>
            <TextField
              size="small"
              multiline
              minRows={2}
              fullWidth
              label="Content"
              value={draftContent}
              onChange={(e) => setDraftContent(e.target.value)}
              placeholder="e.g. 'AI adoption has 5% productivity gain in companies that train' (McKinsey 2024)"
              inputProps={{ 'data-testid': 'resource-content-input' }}
            />
            <TextField
              size="small"
              fullWidth
              label="Source URL (optional)"
              value={draftSourceUrl}
              onChange={(e) => setDraftSourceUrl(e.target.value)}
              inputProps={{ 'data-testid': 'resource-source-url-input' }}
            />
            <Stack direction="row" spacing={1} justifyContent="flex-end">
              <Button
                size="small"
                onClick={() => {
                  reset();
                  setAdding(false);
                }}
                disabled={saving}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                size="small"
                onClick={handleSave}
                disabled={saving || !draftContent.trim()}
                data-testid="resource-save-btn"
              >
                {saving ? 'Saving…' : 'Save'}
              </Button>
            </Stack>
          </Stack>
        </Box>
      )}

      {loading ? (
        <Typography variant="body2" color="text.secondary">
          Loading resources…
        </Typography>
      ) : resources.length === 0 ? (
        <Typography variant="caption" color="text.secondary">
          No resources yet. Add stats, quotes, or links and the AI will weave
          them into generated posts.
        </Typography>
      ) : (
        <Stack spacing={1.5} data-testid="resources-list">
          {resources.map((r) => (
            <Box
              key={r.id}
              sx={{
                p: 1.5,
                border: 1,
                borderColor: 'divider',
                borderRadius: 1,
              }}
              data-testid={`resource-row-${r.id}`}
            >
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.75 }}>
                <Chip
                  size="small"
                  label={RESOURCE_TYPE_LABEL[r.type] ?? r.type}
                />
                {r.reliability && (
                  <Chip
                    size="small"
                    label={r.reliability}
                    color={RELIABILITY_COLOR[r.reliability] ?? 'default'}
                    variant="outlined"
                  />
                )}
                <Box sx={{ flex: 1 }} />
                <IconButton
                  size="small"
                  onClick={() => handleDelete(r)}
                  data-testid={`resource-${r.id}-delete-btn`}
                  title="Delete resource"
                >
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              </Stack>
              <Typography variant="body2" sx={{ mb: 0.5 }}>
                {r.content}
              </Typography>
              {r.sourceUrl && (
                <Typography
                  variant="caption"
                  component="a"
                  href={r.sourceUrl}
                  target="_blank"
                  rel="noreferrer"
                  sx={{ color: 'primary.main', textDecoration: 'none' }}
                >
                  <LinkIcon
                    fontSize="inherit"
                    sx={{ verticalAlign: 'middle', mr: 0.5 }}
                  />
                  {r.sourceUrl}
                </Typography>
              )}
            </Box>
          ))}
        </Stack>
      )}
    </>
  );

  if (inline) {
    return (
      <Box sx={{ p: 2 }} data-testid={TESTID}>
        {body}
      </Box>
    );
  }
  return (
    <Paper variant="outlined" sx={{ p: 3 }} data-testid={TESTID}>
      {body}
    </Paper>
  );
};

export default TopicResourcesSection;
